/**
 * INV-02 — invoice detail, `/stablecoins/invoices/view?id=<invoiceId>`
 * (SPEC-IVC, ALL-5908; page spec `invoice-detail`).
 *
 * The page reads one invoice through the merged hooks — `useInvoice` (invoice +
 * payments + journal, all three or nothing) and `useInvoiceExceptions` — and
 * renders the designed surfaces: loading, a read that failed, the empty
 * sub-sections, and the populated invoice. There is no sample row, no fallback
 * invoice and no fabricated amount anywhere below: every value on screen came
 * from a response, and an absent one renders an explicit dash.
 *
 * ## The 404 rule (no existence oracle)
 *
 * A 404 for an id this organisation does not hold and a 404 for an id that does
 * not exist at all are the same response from the service, so they are the same
 * state here — `classifyReadFailure` collapses both onto `not-found` and the
 * not-found copy says why the page cannot tell them apart. Nothing about the
 * organisation's holdings is inferable from what this page renders.
 *
 * ## Read-only actors and the four-eyes boundary
 *
 * Each mutating control requires its exact invoicing scope from a loaded API
 * effective-access response. Readers retain invoice facts, payments and journal
 * evidence. Server refusals remain authoritative and are rendered verbatim.
 * Approval stays on the separate resolution approval surface.
 *
 * The whole surface sits behind the build-time `invoicing` flag (default OFF) in
 * `App.tsx`, which this page does not touch.
 */

import type { ReactNode } from "react";
import { useInvoicingScope } from "@/features/invoicing/invoices-list/use-invoicing-scope";

import { Link, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/data-display/page-header";
import { Button, buttonVariants } from "@/components/ui/button";
import { SupportDetails } from "@/components/ui/copyable-reference";
import {
  LoadingTable,
  NotAvailableState,
  PermissionDeniedState,
} from "@/components/ui/system-state";
import { WipHint } from "@/components/ui/wip-hint";
import { formatMoney } from "@/features/invoicing/money";
import type { Invoice } from "@/features/invoicing/types";
import {
  DETAIL_COPY,
  classifyReadFailure,
  type ReadFailureKind,
} from "@/features/invoicing/detail/copy";
import { ExceptionsPanel } from "@/features/invoicing/detail/exceptions-panel";
import { InvoiceActions } from "@/features/invoicing/detail/invoice-actions";
import { JournalTimeline } from "@/features/invoicing/detail/journal-timeline";
import { PayLinkPanel } from "@/features/invoicing/detail/pay-link-panel";
import { PaymentsTable } from "@/features/invoicing/detail/payments-table";
import { StatusAxes } from "@/features/invoicing/detail/status-axes";
import { useInvoice, useInvoiceExceptions } from "@/hooks/use-invoices";
import type { ApiErrorInfo } from "@/lib/api";

export default function InvoiceDetailPage() {
  const canExport = useInvoicingScope("invoice:export");
  const [params] = useSearchParams();
  const invoiceId = params.get("id") ?? "";

  return (
    <div data-testid="invoice-detail-page">
      <PageHeader title={DETAIL_COPY.title} subtitle={DETAIL_COPY.subtitle} actions={<>
        <WipHint />
        {canExport && <Link to="/stablecoins/invoices" className={buttonVariants({ variant: "secondary", size: "sm" })}>{DETAIL_COPY.exportReceivablesLabel}</Link>}
      </>} />
      {/* The id scopes every read. With no id nothing is requested at all: a
          read with no subject is a call the gateway has no principal context
          for, and an empty invoice would be a claim no response made. */}
      {invoiceId ? <InvoiceDetailBody invoiceId={invoiceId} /> : <NoInvoiceSelected />}
    </div>
  );
}

/* --------------------------------------------------------------- no id */

function NoInvoiceSelected() {
  return (
    <div data-testid="invoice-no-id">
      <NotAvailableState
        title={DETAIL_COPY.noIdTitle}
        description={DETAIL_COPY.noIdDescription}
        reason={DETAIL_COPY.noIdReason}
      />
      <div className="mt-3 flex justify-center">
        <Link to="/stablecoins/invoices" className="text-sm font-semibold text-primary underline">
          Back to the invoices list
        </Link>
      </div>
    </div>
  );
}

/* ------------------------------------------------------- read failures */

function ReadFailure({
  error,
  reason,
  onRetry,
}: {
  error: ApiErrorInfo;
  reason: ReadFailureKind;
  onRetry: () => void;
}) {
  if (reason === "not-found") {
    // Identical render for an unknown id and an id another organisation holds:
    // the service answers both with one 404 body, and this page does not add a
    // distinction the service did not make.
    return (
      <div
        data-testid="invoice-read-failure"
        data-read-failure="not-found"
        className="flex flex-col items-center gap-2"
      >
        <NotAvailableState title={DETAIL_COPY.notFoundTitle} description={DETAIL_COPY.notFoundDescription} />
        <SupportDetails reference={error.supportReference} capability={error.capability} className="justify-center" />
        <Link to="/stablecoins/invoices" className="text-sm font-semibold text-primary underline">
          Back to the invoices list
        </Link>
      </div>
    );
  }

  if (reason === "denied") {
    return (
      <div data-testid="invoice-read-failure" data-read-failure="denied">
        <PermissionDeniedState
          title="Access required"
          description="This session is not allowed to read this invoice. The service refused the read, so nothing about the invoice is shown."
          details={[{ k: "reasonCode", v: error.reasonCode }]}
          action={
            <Button variant="secondary" onClick={onRetry}>
              {DETAIL_COPY.retry}
            </Button>
          }
          footnote={<SupportDetails reference={error.supportReference} capability={error.capability} />}
        />
      </div>
    );
  }

  return (
    <div
      data-testid="invoice-read-failure"
      data-read-failure="unavailable"
      className="flex flex-col items-center gap-2"
    >
      <NotAvailableState
        title={DETAIL_COPY.readUnavailableTitle}
        description={DETAIL_COPY.readUnavailableDescription}
        reason={error.reasonCode}
      />
      <SupportDetails reference={error.supportReference} capability={error.capability} className="justify-center" />
      <Button variant="secondary" size="sm" onClick={onRetry}>
        {DETAIL_COPY.retry}
      </Button>
    </div>
  );
}

/* ---------------------------------------------------------------- body */

function InvoiceDetailBody({ invoiceId }: { invoiceId: string }) {
  const { data, loading, error, refresh } = useInvoice(invoiceId);
  const exceptions = useInvoiceExceptions({ invoiceId });

  if (loading) {
    return (
      <div data-testid="invoice-loading">
        <LoadingTable rows={5} cols={4} label="Loading invoice" />
      </div>
    );
  }

  if (error) {
    return <ReadFailure error={error} reason={classifyReadFailure(error)} onRetry={refresh} />;
  }

  if (!data) {
    // Settled with neither data nor error: the read never ran. That is the
    // no-subject state, not an empty invoice.
    return <NoInvoiceSelected />;
  }

  const { invoice, payments, journal } = data;

  return (
    <>
      <StatusAxes
        documentStatus={invoice.documentStatus}
        paymentStatus={invoice.paymentStatus}
        paidLate={invoice.paidLate}
        labels={{
          heading: DETAIL_COPY.axesHeading,
          document: DETAIL_COPY.documentAxisLabel,
          payment: DETAIL_COPY.paymentAxisLabel,
          note: DETAIL_COPY.axesNote,
          paidLate: DETAIL_COPY.paidLateFlag,
        }}
      />

      <InvoiceFacts invoice={invoice} />

      <PayLinkPanel invoice={invoice} onChanged={refresh} />

      <InvoiceActions invoice={invoice} onChanged={refresh} />

      <div className="mb-5">
        <PaymentsTable payments={payments} />
      </div>

      <div className="mb-5">
        <ExceptionsPanel
          exceptions={exceptions.data}
          loading={exceptions.loading}
          error={exceptions.error}
          refresh={exceptions.refresh}
        />
      </div>

      <div className="mb-5">
        <JournalTimeline entries={journal} payments={payments} invoice={invoice} />
      </div>
    </>
  );
}

/* ------------------------------------------------------------ fragments */

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 py-2 sm:flex-row sm:gap-3">
      <span className="text-muted sm:w-[140px] sm:shrink-0">{label}</span>
      <span className="min-w-0 break-words font-semibold text-ink">{children}</span>
    </div>
  );
}

function Dash() {
  return <span className="text-muted">{DETAIL_COPY.emptyValue}</span>;
}

/** A wire timestamp rendered exactly as the service sent it — no timezone is invented. */
function Timestamp({ value }: { value: string | null | undefined }) {
  if (!value) return <Dash />;
  return (
    <code dir="ltr" className="font-mono text-[12px]">
      {value}
    </code>
  );
}

function InvoiceFacts({ invoice }: { invoice: Invoice }) {
  return (
    <section aria-label={DETAIL_COPY.totalsHeading} className="mb-4 grid gap-4 lg:grid-cols-2">
      <div className="rounded-lg border border-line bg-surface px-4 py-2">
        <div className="divide-y divide-line text-sm">
          <Fact label={DETAIL_COPY.numberLabel}>{invoice.number ?? <Dash />}</Fact>
          <Fact label={DETAIL_COPY.payerLabel}>
            {invoice.payer ? (
              <span className="inline-flex flex-wrap items-baseline gap-2">
                {invoice.payer.displayName}
                <code dir="ltr" className="font-mono text-[11px] text-muted">
                  {invoice.payer.payerId}
                </code>
              </span>
            ) : (
              <code dir="ltr">{invoice.payerId}</code>
            )}
          </Fact>
          <Fact label="Asset">{invoice.assetSymbol}</Fact>
          <Fact label={DETAIL_COPY.dueDateLabel}>
            <Timestamp value={invoice.dueDate} />
          </Fact>
          <Fact label={DETAIL_COPY.expiresAtLabel}>
            <Timestamp value={invoice.expiresAt} />
          </Fact>
          <Fact label={DETAIL_COPY.memoLabel}>{invoice.memo ?? <Dash />}</Fact>
          <Fact label={DETAIL_COPY.documentHashLabel}>
            {invoice.documentHash ? (
              <code dir="ltr" className="break-all font-mono text-[12px]">
                {invoice.documentHash}
              </code>
            ) : (
              <Dash />
            )}
          </Fact>
        </div>
      </div>

      <div className="rounded-lg border border-line bg-surface px-4 py-3">
        <h2 className="text-sm font-bold text-ink">{DETAIL_COPY.totalsHeading}</h2>
        {/* Every amount is a base-unit string formatted by the BigInt helper:
            no `Number(...)`, no float, no rounding. */}
        <div className="mt-1 divide-y divide-line text-sm">
          <Fact label={DETAIL_COPY.totalsTotalLabel}>
            <strong>{formatMoney(invoice.totals.total)}</strong>{" "}
            <span className="font-normal text-muted">{invoice.assetSymbol}</span>
          </Fact>
          <Fact label={DETAIL_COPY.totalsReceivedLabel}>
            {formatMoney(invoice.totals.received)}{" "}
            <span className="font-normal text-muted">{invoice.assetSymbol}</span>
          </Fact>
          <Fact label={DETAIL_COPY.totalsAppliedLabel}>
            {formatMoney(invoice.totals.applied)}{" "}
            <span className="font-normal text-muted">{invoice.assetSymbol}</span>
          </Fact>
          <Fact label={DETAIL_COPY.totalsExcessLabel}>
            {formatMoney(invoice.totals.excess)}{" "}
            <span className="font-normal text-muted">{invoice.assetSymbol}</span>
          </Fact>
          <Fact label="Version">
            <span className="font-mono text-[12px]">{invoice.version}</span>
          </Fact>
        </div>
        <p className="mt-2 text-xs leading-relaxed text-muted">{DETAIL_COPY.totalsBaseUnitsNote}</p>
      </div>
    </section>
  );
}
